import { TFunction } from 'i18next';
import * as Yup from 'yup';
import _isNaN from 'lodash/isNaN';
import { isAfter, isValid, parseISO } from 'date-fns';
import { getMinutesCountFromTime } from '@lib/utils/getMinutesCountFromTime';
import {
  DOMAIN_REGEX,
  EMAIL_REGEX,
  MAX_MINUTES_VALUE,
  MAX_NUMBER_VALUE,
  MAX_TEXT_FIELD_LENGTH,
} from '@lib/enums/form';
import { SitePointerShape } from '@lib/components/Map/enums';

type GetValidationUtilArgs = {
  allowHyphen?: boolean;
  isArray?: boolean;
  max?: number;
  maxLength?: number;
  message?: string;
  required?: boolean;
  t: TFunction<'translation', undefined>;
};

export function numberRangeValidationFn(
  value?: (number | undefined)[] | undefined,
  required?: boolean,
) {
  // Destructure the input value; assign undefined to min and max if value is not provided
  const [min, max] = value || [undefined, undefined];

  // Check if the input is required but not provided correctly
  if (required && (!Array.isArray(value) || value.length !== 2)) return false;

  // Check for the presence of both min and max values when input is required
  if (required && (min === undefined || max === undefined)) return false;

  // Handle cases where input is not required but one of the values is missing
  if (
    !required &&
    ((min === undefined && max !== undefined) ||
      (max === undefined && min !== undefined))
  )
    return false;

  // If both min and max are undefined and input is not required, consider it valid
  if (!required && min === undefined && max === undefined) return true;

  // Validate that min is less than or equal to max when both are defined
  if (min !== undefined && max !== undefined) return min <= max;

  // Default to false for any other unhandled cases
  return false;
}

export function timeRangeValidationFn(
  value?: (string | undefined)[] | undefined,
  required?: boolean,
) {
  // Destructure the input value; assign undefined to min and max if value is not provided
  const [min, max] = value || [undefined, undefined];

  // Check if the input is required but not provided correctly
  if (required && (!Array.isArray(value) || value.length !== 2)) return false;

  // Check for the presence of both min and max values when input is required
  if (required && (min === undefined || max === undefined)) return false;

  // Handle cases where input is not required but one of the values is missing
  if (
    !required &&
    ((min === undefined && max !== undefined) ||
      (max === undefined && min !== undefined))
  )
    return false;

  // If both min and max are undefined and input is not required, consider it valid
  if (!required && min === undefined && max === undefined) return true;

  return !!(min && max);
}

export function rangeRequiredValidationFn(
  value?: (string | number | undefined)[] | undefined,
  required?: boolean,
) {
  if (required) {
    return (
      Array.isArray(value) &&
      value.length === 2 &&
      value.every((v) => v !== undefined && v !== null)
    );
  }
  return true; // If not required, always pass this test
}

export function getOptionShape<T extends (key: string) => string>(
  t: T,
  required?: boolean,
) {
  if (required) {
    return {
      value: Yup.string().required(t('required-field-error')),
      label: Yup.mixed(),
    };
  }
  return {
    value: Yup.string(),
    label: Yup.mixed(),
  };
}

export const getFieldValidation = ({
  maxLength = MAX_TEXT_FIELD_LENGTH,
  t,
  required,
}: GetValidationUtilArgs) => {
  let validation = Yup.string()
    .trim()
    .nullable()
    .max(maxLength, t('max-text-length-field-error'));
  if (required) validation = validation.required(t('required-field-error'));
  return validation;
};

export const getOptionValidation = ({ t, required }: GetValidationUtilArgs) =>
  required
    ? Yup.object()
        .shape(getOptionShape(t, true))
        .required(t('required-field-error'))
    : Yup.object().shape(getOptionShape(t)).nullable();

export const getRadioButtonGridValidation = ({
  t,
  required,
}: GetValidationUtilArgs) =>
  required
    ? Yup.array()
        .of(Yup.object().shape({}).required(t('required-field-error')))
        .min(1, t('required-field-error'))
        .required(t('required-field-error'))
    : Yup.array().of(Yup.object().shape({})).nullable();

export const getOptionsArrayValidation = ({
  t,
  required,
  message,
}: GetValidationUtilArgs) => {
  const errorMessage = message ?? t('required-field-error');
  return required
    ? Yup.array()
        .of(Yup.object().shape(getOptionShape(t, true)).required(errorMessage))
        .min(1, errorMessage)
        .required(errorMessage)
    : Yup.array()
        .of(Yup.object().shape(getOptionShape(t)))
        .nullable();
};

export const getAttachmentValidation = ({
  t,
  required,
}: GetValidationUtilArgs) =>
  required
    ? Yup.object()
        .shape({ file: Yup.mixed().required(t('required-field-error')) })
        .required(t('required-field-error'))
    : Yup.object().shape({}).nullable();

export const getAttachmentsValidation = ({
  t,
  required,
}: GetValidationUtilArgs) =>
  required
    ? Yup.array()
        .of(
          Yup.object()
            .shape({ file: Yup.mixed().required(t('required-field-error')) })
            .required(t('required-field-error')),
        )
        .min(1, t('required-field-error'))
        .required(t('required-field-error'))
    : Yup.array().of(Yup.object().shape({})).nullable();

export const getOptionsValidation = ({
  isArray,
  t,
  required,
}: GetValidationUtilArgs) => {
  if (isArray) {
    return required
      ? Yup.array()
          .of(Yup.string().nullable())
          .min(1, t('required-field-error'))
          .required(t('required-field-error'))
      : Yup.array().of(Yup.string().nullable()).nullable();
  }
  return required
    ? Yup.object().shape({}).required(t('required-field-error'))
    : Yup.object().shape({}).nullable();
};

export const getTimeValidation = ({ t, required }: GetValidationUtilArgs) => {
  const validation = Yup.string()
    .nullable()
    .test('isValidFormat', t('time-minutes-field-format-error'), (value) => {
      if (!value) return true; // An empty string is acceptable if the field is optional
      return /^\d{1,}:[0-5]\d$/.test(value); // Checking hh:mm format
    })
    .test('isValidTime', t('time-minutes-field-format-error'), (value) => {
      if (!value) return true;
      const [hours, minutes] = value.split(':');
      return (
        Number(hours) >= 0 && Number(minutes) >= 0 && Number(minutes) <= 59
      );
    })
    .test('maxMinutes', t('time-minutes-field-max-error'), (value) => {
      if (!value) return true;
      try {
        const minutesCount = getMinutesCountFromTime(value);
        return minutesCount !== null && minutesCount <= MAX_MINUTES_VALUE;
      } catch (error) {
        return false;
      }
    });

  if (required) return validation.required(t('required-field-error'));
  return validation;
};

export const getEmailValidation = ({ t, required }: GetValidationUtilArgs) => {
  const validation = Yup.string()
    .trim()
    .max(MAX_TEXT_FIELD_LENGTH, t('max-text-length-field-error'))
    .matches(EMAIL_REGEX, t('email-field-error'));

  if (required) return validation.required(t('required-field-error'));
  return validation.nullable();
};

export const getDomainValidation = ({ t, required }: GetValidationUtilArgs) => {
  const validation = Yup.string()
    .trim()
    .max(MAX_TEXT_FIELD_LENGTH, t('max-text-length-field-error'))
    .matches(DOMAIN_REGEX, t('domain-field-error'));

  if (required) return validation.required(t('required-field-error'));
  return validation.nullable();
};

export const getPhoneValidation = ({ t, required }: GetValidationUtilArgs) => {
  if (required) {
    return Yup.object()
      .shape({
        phone: Yup.string().required(t('required-field-error')),
      })
      .required(t('required-field-error'));
  }
  return Yup.object()
    .shape({
      phone: Yup.string().nullable(),
    })
    .nullable();
};

export function getCodeValidation({
  allowHyphen,
  max = 3,
  required,
  t,
}: GetValidationUtilArgs) {
  const maxLengthErrorKey =
    max === MAX_TEXT_FIELD_LENGTH
      ? 'max-text-length-field-error'
      : `max-text-length-field-error-${max}`;
  let schema = Yup.string().trim().max(max, t(maxLengthErrorKey));
  if (!allowHyphen) {
    schema = schema.matches(/^[^-]*$/, t('error-hyphen-not-allowed'));
  }
  if (required) {
    return schema
      .min(1, t('required-field-error'))
      .required(t('required-field-error'));
  }
  return schema.nullable();
}

export function getDefaultNumberValidation({
  max,
  t,
  required,
}: GetValidationUtilArgs) {
  let validation = Yup.string()
    .test('is-number', t('number-field-error'), (value) => !_isNaN(value))
    .test('is-min', t('number-field-error-min-1'), (value) => {
      const number = value ? parseFloat(value) : 0;
      return number >= 0;
    });
  if (max) {
    validation = validation.test(
      'is-max',
      t('number-field-error-max-number'),
      (value) => {
        const number = value ? parseFloat(value) : 0;
        return number <= MAX_NUMBER_VALUE;
      },
    );
  }
  if (required) {
    return validation.required(t('required-field-error'));
  }
  return validation.nullable();
}

export const getPriceValidation = ({ t, required }: GetValidationUtilArgs) => {
  let validation = Yup.string().test(
    'isValidNumber',
    t('number-field-error'),
    (value) => {
      if (!value) return true; // Skip number validation when value is empty
      const number = parseFloat(value);
      return !_isNaN(number) && number >= 0;
    },
  );
  if (required) {
    validation = validation
      .test('is-min', t('number-field-error-min-1'), (value) => {
        const number = value ? parseFloat(value) : 0;
        return number > 0;
      })
      .test('is-max', t('number-field-error-max-number'), (value) => {
        const number = value ? parseFloat(value) : 0;
        return number <= MAX_NUMBER_VALUE;
      })
      .required(t('required-field-error'));
  }
  return validation
    .test('is-min', t('number-field-error-min-1'), (value) => {
      if (!value) return true;
      const number = value ? parseFloat(value) : 0;
      return number > 0;
    })
    .test('is-max', t('number-field-error-max-number'), (value) => {
      if (!value) return true;
      const number = value ? parseFloat(value) : 0;
      return number <= MAX_NUMBER_VALUE;
    })
    .nullable();
};

export const getDateRangeValidation = (
  required: boolean,
  t: TFunction<'translation', undefined>,
) => {
  const validation = Yup.array()
    .of(Yup.string().required(t('required-field-error')))
    .test('is-valid-dates', t('required-field-error'), (value) => {
      if (!Array.isArray(value) || value.length !== 2) {
        return false;
      }

      const start = parseISO(value[0]);
      const end = parseISO(value[1]);

      return isValid(start) && isValid(end) && isAfter(end, start);
    });
  if (required) return validation.required(t('required-field-error'));
  return validation.nullable();
};

// Point coordinates: [longitude, latitude]
const getPointCoordinatesSchema = (t: TFunction<'translation', undefined>) =>
  Yup.array()
    .of(Yup.number().required(t('validation-map-coordinate-required')))
    .min(2, t('validation-map-coordinates-min'))
    .max(2, t('validation-map-coordinates-max'))
    .required(t('validation-map-coordinates-array-required'));

// LineString coordinates: [[lon, lat], [lon, lat], ...]
const getLineStringCoordinatesSchema = (
  t: TFunction<'translation', undefined>,
) =>
  Yup.array()
    .of(
      Yup.array()
        .of(Yup.number().required(t('validation-map-coordinate-required')))
        .min(2, t('validation-map-coordinates-min'))
        .max(2, t('validation-map-coordinates-max')),
    )
    .min(2, t('validation-map-linestring-min-points'))
    .required(t('validation-map-coordinates-array-required'));

// Polygon coordinates: [[[lon, lat], [lon, lat], ...]]
const getPolygonCoordinatesSchema = (t: TFunction<'translation', undefined>) =>
  Yup.array()
    .of(
      Yup.array()
        .of(
          Yup.array()
            .of(Yup.number().required(t('validation-map-coordinate-required')))
            .min(2, t('validation-map-coordinates-min'))
            .max(2, t('validation-map-coordinates-max')),
        )
        .min(3, t('validation-map-polygon-min-points'))
        .test(
          'closed-ring',
          t('validation-map-polygon-closed-ring'),
          (value) => {
            if (!value || value.length < 3) return false;
            const first = value[0];
            const last = value[value.length - 1];
            return (
              first && last && first[0] === last[0] && first[1] === last[1]
            );
          },
        ),
    )
    .min(1, t('validation-map-polygon-min-rings'))
    .required(t('validation-map-coordinates-array-required'));

const getGeometrySchema = (t: TFunction<'translation', undefined>) =>
  Yup.object().shape({
    type: Yup.string()
      .oneOf(
        ['Point', 'LineString', 'Polygon'],
        t('validation-map-geometry-type-invalid'),
      )
      .required(t('validation-map-geometry-type-required')),
    coordinates: Yup.lazy((_, schema) => {
      const parent = schema?.parent as { type?: string };
      const geometryType = parent?.type;

      if (geometryType === 'Point') {
        return getPointCoordinatesSchema(t);
      }
      if (geometryType === 'LineString') {
        return getLineStringCoordinatesSchema(t);
      }
      if (geometryType === 'Polygon') {
        return getPolygonCoordinatesSchema(t);
      }
      // Default fallback
      return Yup.mixed().required(
        t('validation-map-coordinates-array-required'),
      );
    }),
  });

const getPropertiesSchema = (t: TFunction<'translation', undefined>) =>
  Yup.object().shape({
    address: Yup.string().nullable(),
    addressDetails: Yup.string().nullable(),
    shape: Yup.string()
      .oneOf(
        [
          SitePointerShape.Circle,
          SitePointerShape.Pentagon,
          SitePointerShape.Square,
          SitePointerShape.Star,
        ],
        t('validation-map-shape-invalid'),
      )
      .nullable(),
    color: Yup.string().nullable(),
    icon: Yup.mixed().nullable(),
    id: Yup.mixed().nullable(),
    pointCount: Yup.mixed().nullable(),
    sourceables: Yup.mixed().nullable(),
    type: Yup.mixed().nullable(),
  });

const getFeatureSchema = (t: TFunction<'translation', undefined>) =>
  Yup.object().shape({
    type: Yup.string()
      .oneOf(['Feature'], t('validation-map-feature-type-invalid'))
      .required(t('validation-map-feature-type-required')),
    properties: getPropertiesSchema(t),
    geometry: getGeometrySchema(t),
  });

export const getFeatureCollectionValidation = ({
  t,
  required,
}: GetValidationUtilArgs) => {
  const validation = Yup.object().shape({
    type: Yup.string()
      .oneOf(['FeatureCollection'], t('validation-map-collection-type-invalid'))
      .required(t('validation-map-collection-type-required')),

    features: Yup.array()
      .of(getFeatureSchema(t))
      // CRITICAL: This ensures the array is not empty
      .min(1, t('validation-map-location-required'))
      .required(t('validation-map-location-required')),
  });
  if (required) return validation;
  return validation.nullable();
};
